import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Dialog, RadioGroup, FormControl, Radio } from "@primer/react";
import type { DialogProps } from "@primer/react";

interface RoleOption {
  id: string;
  name: string;
  description: string;
}

export interface RoleAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (roleId: string) => Promise<void>;
  eventTitle: string;
  currentRoleId: string | null;
  orgRoleName: string | null;
  roles: RoleOption[];
}

export const RoleAssignmentDialog: React.FC<RoleAssignmentDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  eventTitle,
  currentRoleId,
  orgRoleName,
  roles,
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<string>(currentRoleId ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedRoleId(currentRoleId ?? "");
  }, [currentRoleId, isOpen]);

  const orgDefaultLabel = orgRoleName
    ? `Use org default (${orgRoleName})`
    : "No role override";

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(selectedRoleId);
      onClose();
    } catch {
      // GR-1590 D1: onSave throws on 403/422 (the underlying fetch
      // already toasted the error). Swallow here so the dialog stays
      // open for retry/cancel, rather than letting the rejection bubble
      // as an unhandled promise rejection in the console.
    } finally {
      setSaving(false);
    }
  };

  const footerButtons = useMemo(
    () => [
      { content: "Cancel", onClick: onClose, disabled: saving },
      {
        content: saving ? "Saving..." : "Assign",
        buttonType: "primary" as const,
        onClick: handleSave,
        disabled: saving,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose, saving, selectedRoleId]
  );

  // Body scrolls so the footer stays pinned with many roles. Using
  // `renderBody` over a `[class*="prc-Dialog-Body"]` selector so a Primer
  // minor bump can't silently break it. (Follow-up: greg-editors'
  // GregPrimerTheme still uses class-based selectors for Dialog.Footer
  // border + SelectPanel backdrop — same fragility.)
  const renderBody = useCallback<NonNullable<DialogProps["renderBody"]>>(
    ({ children }) => (
      <Dialog.Body sx={{ maxHeight: "60vh", overflowY: "auto" }}>
        {children}
      </Dialog.Body>
    ),
    []
  );

  if (!isOpen) return null;

  return (
    <Dialog
      title={`Assign Role: ${eventTitle}`}
      onClose={onClose}
      width="large"
      footerButtons={footerButtons}
      renderBody={renderBody}
      sx={{ maxHeight: "85vh" }}
    >
      <RadioGroup name="role_id" onChange={(value) => setSelectedRoleId(value ?? "")}>
        <RadioGroup.Label visuallyHidden>Select a role for this event</RadioGroup.Label>
        <FormControl>
          <Radio value="" name="role_id" checked={selectedRoleId === ""} onChange={() => setSelectedRoleId("")} />
          <FormControl.Label>{orgDefaultLabel}</FormControl.Label>
          <FormControl.Caption>
            {orgRoleName
              ? "Permissions inherited from organization role"
              : "No permissions — assign a specific role below"}
          </FormControl.Caption>
        </FormControl>

        {roles.map((role) => (
          <FormControl key={role.id}>
            <Radio
              value={role.id}
              name="role_id"
              checked={selectedRoleId === role.id}
              onChange={() => setSelectedRoleId(role.id)}
            />
            <FormControl.Label>{role.name}</FormControl.Label>
            {role.description && (
              <FormControl.Caption>{role.description}</FormControl.Caption>
            )}
          </FormControl>
        ))}
      </RadioGroup>
    </Dialog>
  );
};

export default RoleAssignmentDialog;
